import { MUSCLE_GROUP_ARRAY } from "../../../../../constants";
import { buildRirBuckets, buildRpeBuckets } from "./insightsChartHelpers";
import {
  deltaComparedToPrevious,
  forEachPrescribedSetRow,
} from "./insightsSetRowMath";

export const INSIGHTS_MUSCLE_CHART_IDS = Object.freeze([
  "biceps",
  "triceps",
  "back",
  "glutes",
  "abductors",
  "adductors",
  "calves",
  "quadriceps",
  "hamstrings",
  "abs",
  "forearms",
  "chest",
  "shoulders",
]);

export const INSIGHTS_MUSCLE_CHART_ROW_COUNT = INSIGHTS_MUSCLE_CHART_IDS.length;

/**
 * Deprecated muscle keys no longer selectable but still present in stored data.
 * Mapped onto current chart rows so legacy exercises keep counting (no dropped
 * data, no permanently-empty row). "legs" maps to both "quadriceps" and
 * "hamstrings" so the bar chart is consistent with the muscle diagram (which
 * highlights both regions for legacy "Legs" exercises).
 */
const LEGACY_MUSCLE_KEY_ALIASES = Object.freeze({
  legs: ["quadriceps", "hamstrings"],
});

const ALLOWED_EXERCISE_MUSCLE_KEYS = new Set(
  MUSCLE_GROUP_ARRAY.filter((m) => m && m !== "None").map((m) => m.toLowerCase())
);
// Keep legacy values mappable even though they left MUSCLE_GROUP_ARRAY.
for (const legacyKey of Object.keys(LEGACY_MUSCLE_KEY_ALIASES)) {
  ALLOWED_EXERCISE_MUSCLE_KEYS.add(legacyKey);
}

/** Returns all chart ids for a muscle group string, expanding legacy aliases.
 * Use this when you need every id a muscle maps to (e.g. "legs" → ["quadriceps", "hamstrings"]).
 * @param {unknown} raw
 * @returns {string[]}
 */
export function resolveMuscleChartIds(raw) {
  const k = normalizeMuscleKey(raw);
  if (!k || !ALLOWED_EXERCISE_MUSCLE_KEYS.has(k)) return [];
  const alias = LEGACY_MUSCLE_KEY_ALIASES[k];
  if (!alias) return [k];
  return Array.isArray(alias) ? alias : [alias];
}

export const REP_RANGE_BAR_CLASS = Object.freeze({
  r1_5: "ProgramInsights-repBar--r1",
  r6_8: "ProgramInsights-repBar--r2",
  r8_12: "ProgramInsights-repBar--r3",
  r12_15: "ProgramInsights-repBar--r4",
  r15plus: "ProgramInsights-repBar--r5",
});

const REP_RANGE_ORDER = Object.freeze([
  "r1_5",
  "r6_8",
  "r8_12",
  "r12_15",
  "r15plus",
]);

const RPE_AXIS_MIN = 5;
const RPE_AXIS_MAX = 10;

const RPE_STEP_TO_INDEX = new Map(
  buildRpeBuckets(new Array(11).fill(0)).map((b, i) => [b.step, i])
);
const RIR_STEP_TO_INDEX = new Map(
  buildRirBuckets(new Array(11).fill(0)).map((b, i) => [b.step, i])
);

/**
 * Effort bin counts array: 11 numeric RPE/RiR steps (indices 0-10) plus one
 * trailing bin (index 11) for prescribed AMRAP sets, which have no numeric
 * RPE/RiR value.
 */
export const EFFORT_BIN_COUNT = 12;
export const AMRAP_EFFORT_BIN_INDEX = 11;

/** Function to normalize a muscle key.
 * @param {unknown} key
 * @returns {string|null}
 */
export function normalizeMuscleKey(key) {
  if (key == null) {
    return null;
  }
  let s;
  if (typeof key === "string") {
    s = key.trim().toLowerCase();
  } else if (typeof key === "number" || typeof key === "bigint") {
    s = String(key).trim().toLowerCase();
  } else {
    return null;
  }
  if (!s || s === "null" || s === "none") {
    return null;
  }
  return s;
}

/** Maps a muscle group string to a single chart id (first alias for legacy keys).
 * Use resolveMuscleChartIds when you need all ids a legacy key expands to.
 * @param {unknown} raw
 * @returns {string|null}
 */
export function mapExerciseMuscleGroupToChartId(raw) {
  const ids = resolveMuscleChartIds(raw);
  return ids.length > 0 ? ids[0] : null;
}

/** Function to assign a rep range bucket id.
 * @param {number} repValue
 * @returns {keyof typeof REP_RANGE_BAR_CLASS|null}
 */
export function assignRepRangeBucketId(repValue) {
  if (!Number.isFinite(repValue) || repValue < 1) {
    return null;
  }
  if (repValue <= 5) {
    return "r1_5";
  }
  if (repValue <= 8) {
    return "r6_8";
  }
  if (repValue <= 12) {
    return "r8_12";
  }
  if (repValue <= 15) {
    return "r12_15";
  }
  return "r15plus";
}

/** Function to parse a RPE numeric for a histogram.
 * @param {unknown} raw
 * @returns {number|null}
 */
export function parseRpeNumericForHistogram(raw) {
  if (raw == null || raw === "") {
    return null;
  }
  if (raw === "AMRAP") {
    return null;
  }
  const n = Number(raw);
  if (!Number.isFinite(n) || n < RPE_AXIS_MIN) {
    return null;
  }
  return Math.round(Math.min(RPE_AXIS_MAX, n) * 2) / 2;
}

/** Function to parse a RiR numeric for a histogram.
 * @param {unknown} raw
 * @returns {number|null}
 */
export function parseRirNumericForHistogram(raw) {
  const rpe = parseRpeNumericForHistogram(raw);
  if (rpe == null) {
    return null;
  }
  const rir = Math.round((10 - rpe) * 2) / 2;
  if (rir < 0 || rir > 5) {
    return null;
  }
  return rir;
}

/** Function to create empty muscle count maps.
 * @returns {Map<string, number>}
 */
export function createEmptyMuscleCountMaps() {
  const primary = new Map();
  const secondary = new Map();
  for (const id of INSIGHTS_MUSCLE_CHART_IDS) {
    primary.set(id, 0);
    secondary.set(id, 0);
  }
  return { primary, secondary };
}

/** Function to create muscle rows from count maps.
 * @param {Map<string, number>} primary
 * @param {Map<string, number>} secondary
 * @returns {{ id: string, primary: number, secondary: number, delta: null }[]}
 */
export function muscleRowsFromCountMaps(primary, secondary) {
  return INSIGHTS_MUSCLE_CHART_IDS.map((id) => ({
    id,
    primary: primary.get(id) ?? 0,
    secondary: secondary.get(id) ?? 0,
    delta: null,
  }));
}

/** @param {{ primary: number, secondary: number }} row */
function muscleRowTotalSets(row) {
  const p = Number(row?.primary);
  const s = Number(row?.secondary);
  const primary = Number.isFinite(p) ? Math.max(0, p) : 0;
  const secondary = Number.isFinite(s) ? Math.max(0, s) : 0;
  return primary + secondary;
}

/**
 * Week-over-week delta on total sets (primary + secondary) per muscle row.
 * @param {ReturnType<typeof muscleRowsFromCountMaps>} curRows
 * @param {ReturnType<typeof muscleRowsFromCountMaps>|null|undefined} prevRows
 */
export function muscleRowsWithWeekOverWeekDeltas(curRows, prevRows) {
  if (!prevRows?.length) {
    return curRows;
  }
  const prevById = new Map(prevRows.map((row) => [row.id, row]));
  return curRows.map((row) => {
    const prev = prevById.get(row.id);
    const prevTotal = prev ? muscleRowTotalSets(prev) : 0;
    return {
      ...row,
      delta: deltaComparedToPrevious(prevTotal, muscleRowTotalSets(row)),
    };
  });
}

/** Function to create empty rep range counts.
 * @returns {Record<string, number> & { amrap: number }}
 */
export function createEmptyRepRangeCounts() {
  return {
    r1_5: 0,
    r6_8: 0,
    r8_12: 0,
    r12_15: 0,
    r15plus: 0,
    amrap: 0,
  };
}

/** Function to create rep range buckets from counts.
 *
 * `includeAmrap` is off for prescribed data: a prescribed AMRAP set has
 * unknowable reps, so it is counted on the RPE/RiR chart instead and this
 * chart would only render a permanently-empty "AMRAP" column. Outcomes keep
 * it, since a completed AMRAP set with no logged reps still lands here.
 *
 * @param {ReturnType<typeof createEmptyRepRangeCounts>} counts
 * @param {boolean} [includeAmrap]
 * @returns {{ id: string, count: number, barClass: string }[]}
 */
export function repRangeBucketsFromCounts(counts, includeAmrap = true) {
  const numericRows = REP_RANGE_ORDER.map((id) => ({
    id,
    count: counts[id] ?? 0,
    barClass: REP_RANGE_BAR_CLASS[id],
  }));
  if (!includeAmrap) {
    return numericRows;
  }
  return [
    ...numericRows,
    {
      id: "amrap",
      count: counts.amrap ?? 0,
      barClass: "ProgramInsights-repBar--amrap",
    },
  ];
}

/** Function to create effort buckets from bin counts.
 * @param {number[]} binCounts — length `EFFORT_BIN_COUNT`; index `AMRAP_EFFORT_BIN_INDEX` holds the AMRAP count.
 * @param {"rpe"|"rir"} intensityMeasure
 * @returns {{ step: number|"amrap", count: number }[]}
 */
export function effortBucketsFromBinCounts(binCounts, intensityMeasure) {
  const numericBuckets =
    intensityMeasure === "rir"
      ? buildRirBuckets(binCounts)
      : buildRpeBuckets(binCounts);
  return [
    ...numericBuckets,
    { step: "amrap", count: binCounts[AMRAP_EFFORT_BIN_INDEX] ?? 0 },
  ];
}

/** Function to create effort step to index for measure.
 * @param {"rpe"|"rir"} intensityMeasure
 * @returns {Map<number, number>}
 */
export function effortStepToIndexForMeasure(intensityMeasure) {
  return intensityMeasure === "rir" ? RIR_STEP_TO_INDEX : RPE_STEP_TO_INDEX;
}

/** Function to accumulate muscle groups from an exercise.
 * @param {object} ex
 * @param {Map<string, number>} primary
 * @param {Map<string, number>} secondary
 */
export function accumulateMuscleGroupsFromExercise(ex, primary, secondary) {
  const pIds = resolveMuscleChartIds(ex?.primaryMuscleGroup);
  const sIds = resolveMuscleChartIds(ex?.secondaryMuscleGroup);
  const pIdSet = new Set(pIds);
  forEachPrescribedSetRow(ex, (_row, mult) => {
    for (const pId of pIds) {
      primary.set(pId, (primary.get(pId) ?? 0) + mult);
    }
    for (const sId of sIds) {
      if (!pIdSet.has(sId)) {
        secondary.set(sId, (secondary.get(sId) ?? 0) + mult);
      }
    }
  });
}
